import {
  AddCircleOutline,
  ArrowDropUp,
  Close,
  Delete,
  Edit,
  PublishedWithChanges,
} from "@mui/icons-material";
import {
  Box,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Skeleton,
  Tooltip,
  Typography,
} from "@mui/material";
import { FC, useCallback, useEffect, useState } from "react";
import { AddressAPI } from "../../../api";
import AddressForm from "../../../components/address/AddressForm";
import { useAlert } from "../../../lib/alert";
import eventEmitter from "../../../lib/event";
import { Organization } from "../../../model";
import { Address, OrganizationAddressLabels } from "../../../model/Address";

interface ManageAddressProps {
  label: OrganizationAddressLabels;
  organization: Organization;
  customBlankText?: string;
  hideLabelText?: boolean | undefined;
  showStripeSync?: boolean;
}

const ManageAddress: FC<ManageAddressProps> = ({ label, ...props }) => {
  const alert = useAlert();

  const [labelString, setLabelString] = useState<string>("");
  const [address, setAddress] = useState<Address | undefined>();
  const [loaded, setLoaded] = useState<boolean>(false);
  const [expanded, setExpanded] = useState<boolean>(false);

  const handleSubmit = (value: Address) => {
    // If creating a new address
    if (!address) {
      AddressAPI.create(value, props.organization.id, label, "")
        .then(() => {
          alert.success("Successfully created address!");
          setExpanded(false);
          loadAddress();
        })
        .catch((e) => {
          alert.error("Error creating address!");
          console.error(e);
        });
      return;
    }

    AddressAPI.update(address.id, value)
      .then(() => {
        alert.success("Successfully updated address!");
        setExpanded(false);
        loadAddress();
      })
      .catch((e) => {
        alert.error("Error updating address!");
        console.error(e);
      });
  };

  const handleDelete = () => {
    setExpanded(false);
    if (address) {
      AddressAPI.remove(address.id)
        .then(() => {
          loadAddress();
          alert.success("Deleted address sucessfully!");
        })
        .catch((e) => {
          console.error(e);
          alert.error("Failed to delete address.");
        });
    }
  };

  const addressAsString = (address: Address) => {
    return `${address.first_line}, ${address.second_line && address.second_line != "" && address.second_line + ", "}${address.city}, ${address.state} ${address.zip_code}`;
  };

  const loadAddress = useCallback(() => {
    setLoaded(false);
    AddressAPI.getOrganizationAddressByLabel(props.organization.id, label)
      .then((res) => {
        if (res) {
          setAddress(res);
          setLoaded(true);
        } else {
          setAddress(undefined);
          setLoaded(true);
        }
      })
      .catch((e) => {
        console.error(
          "Could not load address for organization " +
            props.organization.id +
            " label " +
            label +
            ": " +
            e,
        );
      });
  }, [label, props.organization.id]);

  const updateLabelString = useCallback(() => {
    if (label == OrganizationAddressLabels.BILLING)
      setLabelString(!props.hideLabelText ? "Billing Address" : "Address");
    else if (label == OrganizationAddressLabels.PRIMARY)
      setLabelString(!props.hideLabelText ? "Primary Address" : "Address");
    else if (label == OrganizationAddressLabels.SECONDARY)
      setLabelString(!props.hideLabelText ? "Secondary Address" : "Address");
  }, [label, props.hideLabelText]);

  useEffect(() => {
    updateLabelString();
    loadAddress();
  }, [loadAddress, updateLabelString]);

  useEffect(() => {
    eventEmitter.on("subscription:updated", () => {
      loadAddress();
    });
    return () => {
      eventEmitter.off("subscription:updated", () => {
        loadAddress();
      });
    };
  }, [loadAddress]);

  return (
    <>
      <Grid container size={12}>
        <Grid size={3}>
          <Box
            height="100%"
            display="flex"
            alignItems="center"
            sx={{
              backgroundColor: "#fafafa",
              borderRight: "1px solid #aaaaaa",
              px: "9px",
              py: "7px",
            }}
          >
            <Typography>
              <b>{labelString}</b>
            </Typography>
            {props.showStripeSync && (
              <Tooltip
                sx={{ ml: 1 }}
                title="Your address is synced with Stripe"
              >
                <IconButton size="small">
                  <PublishedWithChanges />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Grid>
        <Grid size={7}>
          <Box
            height="100%"
            display="flex"
            alignItems="center"
            sx={{ px: "9px", py: "7px", backgroundColor: "#ffffff" }}
          >
            {!address || !loaded ? (
              !loaded ? (
                <Skeleton width={"75%"} />
              ) : (
                <Typography>
                  <span style={{ color: "#888888" }}>
                    <i>
                      {props.customBlankText ||
                        `Add ${labelString.toLowerCase() || "address"}`}
                    </i>
                  </span>
                </Typography>
              )
            ) : (
              <Typography>{addressAsString(address)}</Typography>
            )}
          </Box>
        </Grid>

        <Grid size={1}>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
            sx={{
              background: "#ffffff",
            }}
          >
            {address && !expanded && (
              <IconButton onClick={() => setExpanded(true)}>
                <Edit />
              </IconButton>
            )}
            {address && expanded && (
              <IconButton onClick={() => setExpanded(false)}>
                <ArrowDropUp />
              </IconButton>
            )}
          </Box>
        </Grid>

        <Grid size={1}>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="55px"
            sx={{
              background: "#ffffff",
            }}
          >
            {!address && (
              <IconButton onClick={() => setExpanded(!expanded)}>
                {!expanded ? <AddCircleOutline /> : <Close />}
              </IconButton>
            )}
            {address && (
              <IconButton onClick={handleDelete}>
                <Delete />
              </IconButton>
            )}
          </Box>
        </Grid>

        <Grid container margin={0} padding={0} minHeight={0} size={12}>
          <Collapse
            in={expanded}
            unmountOnExit
            sx={{ m: 0, p: 0, width: "100%" }}
          >
            <Divider />
            <Box p={2}>
              <AddressForm
                current={address}
                onSubmit={handleSubmit}
                onCancel={() => setExpanded(false)}
              />
            </Box>
          </Collapse>
        </Grid>
      </Grid>
    </>
  );
};

export default ManageAddress;
